/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from 'react';
import { 
  Smartphone, 
  Download, 
  X, 
  CheckCircle, 
  Share2, 
  PlusSquare, 
  Sparkles, 
  Layers, 
  ExternalLink,
  ShieldCheck,
  Check,
  Globe,
  ArrowRight
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

interface MobileInstallModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function MobileInstallModal({ isOpen, onClose }: MobileInstallModalProps) {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [activeOS, setActiveOS] = useState<'android' | 'ios'>('android');
  const [downloadSuccess, setDownloadSuccess] = useState(false);

  useEffect(() => {
    // Detect mobile OS
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) {
      setActiveOS('ios');
    } else {
      setActiveOS('android');
    }

    // Listen for beforeinstallprompt event for 1-click install on Android Chrome
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Check if app is running as standalone PWA
    if (window.matchMedia('(display-mode: standalone)').matches || (navigator as any).standalone) {
      setIsInstalled(true);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstalled(true);
        setDownloadSuccess(true);
      }
      setDeferredPrompt(null);
    } else {
      // Create a web app shortcut download for Android devices
      triggerShortcutDownload();
    }
  };

  const triggerShortcutDownload = () => {
    const appUrl = window.location.href;
    const content = `[InternetShortcut]\nURL=${appUrl}\nIDList=\nIconIndex=0\nHotKey=0`;
    const blob = new Blob([content], { type: 'text/plain' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'Quality_Unidosis_App.url';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    setDownloadSuccess(true);
    setTimeout(() => setDownloadSuccess(false), 5000);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 overflow-y-auto font-sans" id="mobile-install-modal">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs transition-opacity"
          onClick={onClose}
        ></div>

        {/* Modal Wrapper */}
        <div className="flex min-h-full items-center justify-center p-3 sm:p-6 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 15 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 15 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-lg transform overflow-hidden rounded-3xl bg-white p-6 sm:p-8 text-left shadow-2xl border border-slate-200 transition-all space-y-6"
          >
            {/* Header */}
            <div className="flex items-start justify-between border-b border-slate-100 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-teal-50 text-teal-700 rounded-2xl border border-teal-150">
                  <Smartphone className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-black text-slate-900 uppercase tracking-tight leading-tight">
                    Descargar App Móvil (Android / iOS)
                  </h3>
                  <p className="text-xs text-slate-500 font-sans mt-0.5">
                    Instalación nativa directa en tu celular sin Play Store / App Store
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-slate-400 hover:text-slate-600 p-1.5 rounded-full hover:bg-slate-100 transition-colors cursor-pointer"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Notification Badge */}
            {downloadSuccess && (
              <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-2xl text-xs font-bold flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-emerald-600 shrink-0" />
                <span>¡Acceso directo a la App generado e instalado correctamente!</span>
              </div>
            )}

            {/* OS Selector Tabs */}
            <div className="flex bg-slate-100 p-1 rounded-2xl">
              <button
                onClick={() => setActiveOS('android')}
                className={`flex-1 py-2.5 text-center text-xs font-black rounded-xl transition-all cursor-pointer flex items-center justify-center gap-2 ${
                  activeOS === 'android'
                    ? 'bg-white text-emerald-700 shadow-sm border border-emerald-100'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                <Smartphone className="h-4 w-4 text-emerald-600" />
                <span>Android (APK / WebAPK)</span>
              </button>
              <button
                onClick={() => setActiveOS('ios')}
                className={`flex-1 py-2.5 text-center text-xs font-black rounded-xl transition-all cursor-pointer flex items-center justify-center gap-2 ${
                  activeOS === 'ios'
                    ? 'bg-white text-indigo-700 shadow-sm border border-indigo-100'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                <Smartphone className="h-4 w-4 text-indigo-600" />
                <span>iPhone / iPad (iOS)</span>
              </button>
            </div>

            {/* Android Instructions */}
            {activeOS === 'android' && (
              <div className="space-y-4">
                <div className="bg-emerald-50/70 border border-emerald-200/80 rounded-2xl p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-black uppercase text-emerald-900 bg-emerald-100 px-2.5 py-0.5 rounded-full tracking-wider">
                      OPCIÓN 1: INSTALACIÓN AUTOMÁTICA
                    </span>
                    <ShieldCheck className="h-4 w-4 text-emerald-600" />
                  </div>
                  <p className="text-xs text-slate-700 font-medium leading-relaxed">
                    Presiona el botón a continuación para descargar e instalar el acceso directo WebAPK nativo de <strong>Quality Unidosis</strong> en tu dispositivo Android.
                  </p>
                  
                  <button
                    onClick={handleInstallClick}
                    className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all cursor-pointer shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                    id="btn-trigger-android-pwa-install"
                  >
                    <Download className="h-4.5 w-4.5" />
                    <span>Instalar App en Celular Android</span>
                  </button>
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-3">
                  <span className="text-[10px] font-black uppercase text-slate-600 bg-slate-200/70 px-2.5 py-0.5 rounded-full tracking-wider">
                    OPCIÓN 2: PASOS MANUALES EN CHROME
                  </span>
                  <ol className="text-xs text-slate-600 space-y-2 list-decimal list-inside font-medium">
                    <li>Abre esta página en Google Chrome o tu navegador Android.</li>
                    <li>Toca el menú de opciones de Chrome (tres puntos verticales <strong>⋮</strong> arriba a la derecha).</li>
                    <li>Selecciona <strong>"Agregar a la pantalla principal"</strong> o <strong>"Instalar aplicación"</strong>.</li>
                    <li>Confirma haciendo clic en <strong>"Instalar"</strong>. Listo, la App aparecerá en tu menú de aplicaciones.</li>
                  </ol>
                </div>
              </div>
            )}

            {/* iOS Instructions */}
            {activeOS === 'ios' && (
              <div className="space-y-4">
                <div className="bg-indigo-50/70 border border-indigo-200/80 rounded-2xl p-4 space-y-3">
                  <span className="text-[10px] font-black uppercase text-indigo-900 bg-indigo-100 px-2.5 py-0.5 rounded-full tracking-wider">
                    PASOS DE INSTALACIÓN EN SAFARI (IPHONE / IPAD)
                  </span>
                  <div className="space-y-3 text-xs text-slate-700 font-medium">
                    <div className="flex items-start gap-3 bg-white p-3 rounded-xl border border-indigo-100">
                      <div className="p-2 bg-indigo-100 text-indigo-700 rounded-lg shrink-0 font-bold">1</div>
                      <div>
                        <p className="font-bold text-slate-900">Abre Safari</p>
                        <p className="text-[11px] text-slate-500">Asegúrate de estar navegando en Safari en tu iPhone o iPad.</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 bg-white p-3 rounded-xl border border-indigo-100">
                      <div className="p-2 bg-indigo-100 text-indigo-700 rounded-lg shrink-0 font-bold">2</div>
                      <div>
                        <p className="font-bold text-slate-900 flex items-center gap-1.5">
                          <span>Toca el botón Compartir</span>
                          <Share2 className="h-4 w-4 text-indigo-600 inline" />
                        </p>
                        <p className="text-[11px] text-slate-500">Ubicado en el menú inferior central de tu navegador Safari.</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 bg-white p-3 rounded-xl border border-indigo-100">
                      <div className="p-2 bg-indigo-100 text-indigo-700 rounded-lg shrink-0 font-bold">3</div>
                      <div>
                        <p className="font-bold text-slate-900 flex items-center gap-1.5">
                          <span>Selecciona "Agregar a inicio"</span>
                          <PlusSquare className="h-4 w-4 text-indigo-600 inline" />
                        </p>
                        <p className="text-[11px] text-slate-500">Desplázate hacia abajo en el menú de opciones y presiona "Agregar a inicio".</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 bg-white p-3 rounded-xl border border-indigo-100">
                      <div className="p-2 bg-indigo-100 text-indigo-700 rounded-lg shrink-0 font-bold">4</div>
                      <div>
                        <p className="font-bold text-slate-900">Confirma "Agregar"</p>
                        <p className="text-[11px] text-slate-500">Toca "Agregar" en la esquina superior derecha para crear el ícono de la App en tu pantalla de inicio.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Footer Features */}
            <div className="border-t border-slate-100 pt-4 flex flex-wrap items-center justify-between text-[11px] text-slate-500 gap-2 font-mono">
              <span className="flex items-center gap-1 text-emerald-700 font-bold">
                <Check className="h-3.5 w-3.5 text-emerald-600" />
                <span>100% Compatible Celular</span>
              </span>
              <span className="flex items-center gap-1 text-sky-700 font-bold">
                <Globe className="h-3.5 w-3.5 text-sky-600" />
                <span>Sincronización Nube Neiva</span>
              </span>
            </div>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="w-full py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl transition-colors cursor-pointer"
            >
              Entendido / Cerrar
            </button>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  );
}
